/**
 * HESAP VE VARLIK SINIRLARI (gelir modeli; kullanıcı kararları 20 ve
 * 26.10.2026):
 *   - Ücretsiz kasada en çok 2 (etkin) hesap; ek hesap alınınca artar.
 *   - Hesap başına en çok 5 farklı varlık; tamamen satılan varlık yerini
 *     boşaltır. İlk ek hesapla bu sınır bütün hesaplarda kalkar.
 *   - Süre bitince sınırı aşan kısım silinmez: düzenlenebilir kalacak
 *     hesapları kullanıcı seçer, diğerleri salt okunur. Varlık sınırını
 *     aşan hesapta satış ve para çekme girilir, yeni varlık alımı girilmez.
 *
 * Sayılar ondalık metinle toplanır (CLAUDE.md §6: para/miktar `number` değil).
 */

import type { AssetBinding, Transaction } from "@parafon/contracts";
import { POSITION_SIGN, type Account, type NewTransaction } from "@parafon/contracts/local-api";
import { decimalNegate, decimalSign, decimalSum } from "../lib/decimal";
import { M } from "../dil";

/** Süre bitince düzenlenebilir kalacak hesaplar (kullanıcının seçimi, kimlik listesi). */
export const DUZENLENEBILIR_HESAPLAR_AYARI = "kabiliyet.duzenlenebilirHesaplar";

interface HareketDeposu {
  readonly transactions: {
    query(f?: { accountIds?: readonly string[] }): Promise<{ rows: readonly Transaction[] }>;
  };
}

export function varlikAnahtari(a: AssetBinding): string {
  return a.kind === "tracked" ? `tracked:${a.instrumentId}` : `manual:${a.label}|${a.currency}`;
}

/** Hesapta bugün elde tutulan (net miktarı sıfırdan büyük) varlıklar. */
export async function eldekiVarliklar(
  repo: HareketDeposu,
  accountId: string,
  haricHareket: string | null = null,
): Promise<Set<string>> {
  const { rows } = await repo.transactions.query({ accountIds: [accountId] });
  const miktarlar = new Map<string, string[]>();
  for (const t of rows) {
    if (t.id === haricHareket) continue;
    const isaret = POSITION_SIGN[t.type];
    if (isaret === 0) continue;
    const k = varlikAnahtari(t.asset);
    const liste = miktarlar.get(k) ?? [];
    liste.push(isaret > 0 ? t.quantity : decimalNegate(t.quantity));
    miktarlar.set(k, liste);
  }
  const eldeki = new Set<string>();
  for (const [k, liste] of miktarlar) if (decimalSign(decimalSum(liste)) > 0) eldeki.add(k);
  return eldeki;
}

/**
 * Bu hareket kaydedilirse varlık sınırı aşılır mı? `null` = engel yok.
 * Yalnızca pozisyonu ARTIRAN hareket (alış, bedelsiz, rüçhan, bölünme,
 * gelen transfer) ve yalnızca hesapta OLMAYAN bir varlık için engellenir:
 * satış, para çekme ve eldeki varlığa ek alım her zaman serbest.
 */
export async function varlikSiniriEngeli(
  repo: HareketDeposu,
  sinir: number | null,
  hareket: Pick<NewTransaction, "accountId" | "asset" | "type">,
  duzenlenen: string | null,
): Promise<string | null> {
  if (sinir === null || POSITION_SIGN[hareket.type] <= 0) return null;
  const eldeki = await eldekiVarliklar(repo, hareket.accountId, duzenlenen);
  if (eldeki.has(varlikAnahtari(hareket.asset))) return null;
  if (eldeki.size < sinir) return null;
  return M.ayarlar.kilit.varlikSiniri(eldeki.size, sinir);
}

/** Hesap sınırı: yeni hesap (ya da devre dışı bir hesabı yeniden açmak) mümkün mü. */
export function hesapEklenebilir(etkinHesapSayisi: number, sinir: number | null): boolean {
  return sinir === null || etkinHesapSayisi < sinir;
}

/**
 * Düzenlenebilir hesaplar. Sınır yoksa ya da etkin hesaplar sınırı aşmıyorsa
 * `null` (hepsi düzenlenebilir). Aşıyorsa kullanıcının seçtikleri (etkin
 * olanlar, sınır kadar); seçim eksikse listedeki sırayla tamamlanır — hiçbir
 * zaman sınırdan az hesap düzenlenebilir kalmaz.
 */
export function duzenlenebilirHesaplar(
  etkin: readonly Pick<Account, "id">[],
  secim: readonly string[] | null,
  sinir: number | null,
): ReadonlySet<string> | null {
  if (sinir === null || etkin.length <= sinir) return null;
  const etkinKimlikler = etkin.map((a) => a.id);
  const sonuc = new Set<string>();
  for (const id of secim ?? []) {
    if (sonuc.size >= sinir) break;
    if (etkinKimlikler.includes(id)) sonuc.add(id);
  }
  for (const id of etkinKimlikler) {
    if (sonuc.size >= sinir) break;
    sonuc.add(id);
  }
  return sonuc;
}

export function secimOku(deger: string | null): string[] | null {
  if (deger === null) return null;
  try {
    const v: unknown = JSON.parse(deger);
    return Array.isArray(v) && v.every((x) => typeof x === "string") ? (v as string[]) : null;
  } catch {
    return null;
  }
}
